/**
 * useLangTransition — two-phase fade+scale transition for language/direction switches.
 *
 * Flow:
 *   1. Add `.lang-transition-out` to document.body (opacity 1→0, scale 1→0.97, 230ms)
 *   2. After 230ms: call onSwap() to mutate lang/dir, swap out→in class
 *   3. After 400ms: remove `.lang-transition-in`
 *
 * Applies classes to document.body so the transition is global across all routes
 * without any shell or wrapper changes.
 *
 * Respects prefers-reduced-motion: calls onSwap() immediately, skips animation.
 */
import { useCallback } from "react";
import { useReducedMotion } from "./useReducedMotion";

const OUT_DURATION = 230;
const IN_DURATION  = 400;

export function useLangTransition() {
  const prefersReducedMotion = useReducedMotion();

  /** Call triggerTransition(onSwap) — onSwap runs at the midpoint of the animation. */
  const triggerTransition = useCallback(
    (onSwap: () => void) => {
      if (prefersReducedMotion) {
        onSwap();
        return;
      }

      document.body.classList.add("lang-transition-out");

      const swapTimer = window.setTimeout(() => {
        onSwap();
        document.body.classList.remove("lang-transition-out");
        document.body.classList.add("lang-transition-in");

        const cleanTimer = window.setTimeout(() => {
          document.body.classList.remove("lang-transition-in");
        }, IN_DURATION);

        return cleanTimer;
      }, OUT_DURATION);

      return swapTimer;
    },
    [prefersReducedMotion],
  );

  return { triggerTransition } as const;
}
